import "@/App.css";
import { useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth, apiErr } from "@/context/AuthContext";
import Login from "@/pages/Login";
import Research from "@/pages/Research";
import Library from "@/pages/Library";
import Jobs from "@/pages/Jobs";
import Audit from "@/pages/Audit";
import Users from "@/pages/Users";

function Protected({ children, roles }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-sm text-muted-foreground">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/research" replace />;
  return children;
}

// Handles the Emergent Google OAuth return: {origin}/research#session_id=...
function GoogleCallback() {
  const { loginWithSession } = useAuth();
  const nav = useNavigate();
  const done = useRef(false);
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const sid = new URLSearchParams(window.location.hash.replace(/^#/, "")).get("session_id");
    window.history.replaceState(null, "", window.location.pathname);
    (async () => {
      try {
        await loginWithSession(sid);
        nav("/research", { replace: true });
      } catch (err) {
        toast.error(apiErr(err));
        nav("/login", { replace: true });
      }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return <div className="min-h-screen flex items-center justify-center text-sm text-muted-foreground" data-testid="google-callback">Signing you in…</div>;
}

function AppRoutes() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <GoogleCallback />;
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/research" element={<Protected><Research /></Protected>} />
      <Route path="/library" element={<Protected><Library /></Protected>} />
      <Route path="/jobs" element={<Protected roles={["super_admin", "admin", "reviewer"]}><Jobs /></Protected>} />
      <Route path="/users" element={<Protected roles={["super_admin", "admin"]}><Users /></Protected>} />
      <Route path="/audit" element={<Protected roles={["super_admin", "admin"]}><Audit /></Protected>} />
      <Route path="*" element={<Navigate to="/research" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </div>
  );
}

export default App;
